import { Container, Graphics, Sprite, type Spritesheet, type Texture } from "pixi.js";
import { Spine, type SkeletonData } from "@esotericsoftware/spine-pixi-v8";
import { playClipAndWait } from "@slot/presentation";
import type { Log, PresentationTaskRunner, SpineDriver } from "@slot/presentation";
import { FittedLine, TEXT } from "../../views/textStyles";
import type { VsWildTuning } from "../../tuning/vsWildTuning";

/**
 * The `expanding-wild-vs-spine` prefab (`game-shared-visuals/expanding-wild/vs/`), y-down, from the prefab dump: a
 * 206×958.1 board (the reveal grows its height from the centre), the board's art clipped to it (the `bg` plank, the
 * `horizontal` skeleton at 0.508, the two label glows), the golden border lines on its four edges, and the two
 * multiplier labels above it all, unclipped (they slide in from beside the reel).
 */
const PREFAB = {
  size: { width: 206, height: 958.1 },
  bg: { frame: "bg", width: 236.9, height: 969.7 },
  spineScale: 0.508,
  glow: { frame: "glow", width: 295.9, height: 144, alpha: 130 / 255, topY: -373.86, bottomY: 385.54 },
  label: { box: { width: 183, height: 95.5 }, fontSize: 85, topY: -398.07, bottomY: 398.07 },
  /** The top and bottom lines: each edge's line sits this far beyond the edge; the bottom one is mirrored and off-centre. */
  edge: { frame: "horizontal-line", width: 227, height: 37.9, topBeyond: 0.88, bottomBeyond: 2.41, bottomX: 9.35 },
  /** The side lines: at ±x, as tall as the board plus `extraHeight`. */
  side: { frame: "vertical-line", width: 42.3, x: 99.41, extraHeight: 32.16 },
} as const;

/** Where the top label rests, in global space, for a value flying in to become it (the Super Bonus saved total). */
export interface VsWildLabelTarget {
  readonly x: number;
  readonly y: number;
  /** The label's global scale (its box against the design units it was authored in). */
  readonly scale: number;
}

/**
 * One battle's board over its reel (Cocos `VsSpineWildView`): the centre-out reveal, then the clip chosen by the
 * winner with the labels' choreography beside it (both labels in, the loser fades, the winner to the centre). The
 * labels run on the runner, the clip on the Spine driver at the scope's rate; a slam of the scope fast-finishes the
 * labels and snaps the clip to its last frame, so the board always ends on the result.
 */
export class VsWildView extends Container {
  /** The prefab's board size, for the layer's fit to the reel. */
  public static readonly authoredSize = PREFAB.size;

  private readonly board: Container;
  private readonly boardMask: Graphics;
  private readonly spine: Spine | null;
  private readonly glows: readonly Sprite[];
  private readonly topLabel: FittedLine;
  private readonly bottomLabel: FittedLine;
  private readonly topEdge: Sprite | null;
  private readonly bottomEdge: Sprite | null;
  private readonly sides: readonly Sprite[];
  private readonly log: Log;

  public constructor(input: {
    skeletonData: SkeletonData | null;
    /** `expanding-vs-wild-2`: the plank and the border lines. */
    art: Spritesheet | null;
    /** `expanded-wild-vs`: the label glow. */
    glowArt: Spritesheet | null;
    spineDriver: SpineDriver;
    log: Log;
  }) {
    super({ label: "VsWild" });
    this.log = input.log;
    const art = (frame: string): Texture | null => input.art?.textures[frame] ?? null;

    this.board = new Container({ label: "Board" });
    this.boardMask = new Graphics();
    this.boardMask.label = "BoardMask";
    this.board.mask = this.boardMask;
    const bg = sprite(art(PREFAB.bg.frame), "Bg", PREFAB.bg.width, PREFAB.bg.height);
    if (bg) this.board.addChild(bg);

    this.spine = input.skeletonData ? new Spine({ skeletonData: input.skeletonData, autoUpdate: false }) : null;
    if (this.spine) {
      this.spine.label = "Battle";
      this.spine.scale.set(PREFAB.spineScale);
      input.spineDriver.attach(this.spine);
      this.board.addChild(this.spine);
    } else {
      this.log("[VsWildView] no `horizontal` skeleton — the battle shows its labels only");
    }

    const glowTexture = input.glowArt?.textures[PREFAB.glow.frame] ?? null;
    this.glows = [PREFAB.glow.topY, PREFAB.glow.bottomY].flatMap((y, i) => {
      const glow = sprite(glowTexture, i === 0 ? "GlowTop" : "GlowBottom", PREFAB.glow.width, PREFAB.glow.height);
      if (!glow) return [];
      glow.y = y;
      this.board.addChild(glow);
      return [glow];
    });

    this.topEdge = sprite(art(PREFAB.edge.frame), "EdgeTop", PREFAB.edge.width, PREFAB.edge.height);
    this.bottomEdge = sprite(art(PREFAB.edge.frame), "EdgeBottom", PREFAB.edge.width, PREFAB.edge.height);
    if (this.bottomEdge) {
      this.bottomEdge.scale.x *= -1;
      this.bottomEdge.x = PREFAB.edge.bottomX;
    }
    this.sides = [-PREFAB.side.x, PREFAB.side.x].flatMap((x, i) => {
      const side = sprite(art(PREFAB.side.frame), i === 0 ? "SideLeft" : "SideRight", PREFAB.side.width, PREFAB.size.height + PREFAB.side.extraHeight);
      if (!side) return [];
      side.x = x;
      return [side];
    });

    const overlay = new Container({ label: "Labels" });
    // The prefab's labels are Roboto Bold (outline 2, shadow blur 10).
    const labelSpec = { style: TEXT.multiplier, fontSize: PREFAB.label.fontSize, box: PREFAB.label.box };
    this.topLabel = new FittedLine({ ...labelSpec, y: PREFAB.label.topY, label: "MultiplierTop" });
    this.bottomLabel = new FittedLine({ ...labelSpec, y: PREFAB.label.bottomY, label: "MultiplierBottom" });
    overlay.addChild(this.topLabel, this.bottomLabel);

    // The prefab's order: the board, the labels, then the border lines over both.
    this.addChild(this.board, this.boardMask, overlay, ...[this.topEdge, this.bottomEdge].filter((s): s is Sprite => s !== null), ...this.sides);
    this.setBoardHeight(PREFAB.size.height);
  }

  /** Collapsed to the reveal's start: the clip cleared to its setup pose and hidden, the labels and glows hidden. */
  public prepareHidden(startHeight: number): void {
    this.setBoardHeight(startHeight);
    if (this.spine) {
      this.spine.state.clearTracks();
      this.spine.skeleton.setToSetupPose();
      this.spine.visible = false;
    }
    for (const label of [this.topLabel, this.bottomLabel]) {
      label.visible = false;
      label.alpha = 1;
    }
    for (const glow of this.glows) {
      glow.visible = false;
      glow.alpha = PREFAB.glow.alpha;
    }
  }

  /** The two values as shown (the top contender first). */
  public setMultipliers(top: string, bottom: string): void {
    this.topLabel.setLine(top);
    this.bottomLabel.setLine(bottom);
  }

  /** The top label's resting spot (valid once the layer has placed the board). */
  public topLabelTarget(): VsWildLabelTarget {
    const home = this.toGlobal({ x: 0, y: PREFAB.label.topY });
    const unit = this.toGlobal({ x: 1, y: PREFAB.label.topY });
    return { x: home.x, y: home.y, scale: Math.hypot(unit.x - home.x, unit.y - home.y) * this.topLabel.scale.x };
  }

  /** The centre-out reveal: the board's height from its start to `targetHeight`. */
  public async playReveal(input: { runner: PresentationTaskRunner; tuning: VsWildTuning; scope: string }): Promise<void> {
    const { startHeight, expandHeight, revealSeconds, revealEasing } = input.tuning;
    await input.runner.tweenProgress(revealSeconds, (t) => this.setBoardHeight(startHeight + (expandHeight - startHeight) * t), input.scope, { easing: revealEasing });
    this.setBoardHeight(expandHeight);
  }

  /**
   * The battle: the clip for the winner and the labels' choreography together; resolves when both are done. On a
   * slam both fast-finish and the clip snaps to its end. `topLabelEntrance` replaces the top label's slide when it
   * resolves true (a value flew in and became the label).
   */
  public async playBattle(input: {
    winningIndex: 0 | 1;
    runner: PresentationTaskRunner;
    tuning: VsWildTuning;
    scope: string;
    topLabelEntrance?: () => Promise<boolean>;
  }): Promise<void> {
    const { runner, tuning, scope, winningIndex } = input;
    const clip = tuning.animationNames[winningIndex];
    const spine = this.spine;
    if (spine) spine.visible = true;
    await Promise.all([
      spine
        ? playClipAndWait(spine, runner, clip, { scope }).then((played) => {
            if (!played) this.log(`[VsWildView] the skeleton has no clip "${clip}"`);
          })
        : Promise.resolve(),
      this.playLabels(input),
    ]);
    // The last frame: a no-op after a natural finish, the result after a slam cut the clip short.
    const entry = spine?.state.getCurrent(0);
    if (spine && entry) {
      entry.trackTime = entry.animationEnd;
      spine.update(0);
    }
  }

  private async playLabels(input: { winningIndex: 0 | 1; runner: PresentationTaskRunner; tuning: VsWildTuning; scope: string; topLabelEntrance?: () => Promise<boolean> }): Promise<void> {
    const { runner, tuning, scope, winningIndex } = input;
    for (const glow of this.glows) glow.visible = true;
    const enterTop = async (): Promise<void> => {
      if (input.topLabelEntrance && (await input.topLabelEntrance())) {
        this.topLabel.position.set(0, PREFAB.label.topY);
        this.topLabel.visible = true;
        return;
      }
      await this.slideIn(this.topLabel, PREFAB.label.topY, -tuning.entranceOffset, runner, tuning, scope);
    };
    await Promise.all([enterTop(), this.slideIn(this.bottomLabel, PREFAB.label.bottomY, tuning.entranceOffset, runner, tuning, scope)]);
    await runner.sleep(tuning.clashResolveDelaySeconds, scope);
    const [winner, loser] = winningIndex === 0 ? [this.topLabel, this.bottomLabel] : [this.bottomLabel, this.topLabel];
    await fadeOut(loser, 1, tuning.loserFadeSeconds, runner, scope);
    await runner.sleep(tuning.preWinnerMoveDelaySeconds, scope);
    const fromY = winner.y;
    await Promise.all([
      runner.tweenProgress(tuning.winnerMoveSeconds, (t) => (winner.y = fromY * (1 - t)), scope, { easing: tuning.winnerMoveEasing }),
      ...this.glows.map((glow) => fadeOut(glow, PREFAB.glow.alpha, tuning.loserFadeSeconds, runner, scope)),
    ]);
    winner.y = 0;
    if (tuning.holdSeconds > 0) await runner.sleep(tuning.holdSeconds, scope);
  }

  /** A label slides in to its home from `offsetX` beside it. */
  private async slideIn(label: FittedLine, homeY: number, offsetX: number, runner: PresentationTaskRunner, tuning: VsWildTuning, scope: string): Promise<void> {
    label.position.set(offsetX, homeY);
    label.visible = true;
    await runner.tweenProgress(tuning.entranceSeconds, (t) => (label.x = offsetX * (1 - t)), scope, { easing: tuning.entranceEasing });
    label.x = 0;
  }

  /** The board at `height`: the clip's window, and the border lines on its edges. */
  private setBoardHeight(height: number): void {
    const h = Math.max(0, height);
    const { width } = PREFAB.size;
    this.boardMask.clear().rect(-width / 2, -h / 2, width, h).fill(0xffffff);
    if (this.topEdge) this.topEdge.y = -(h / 2 + PREFAB.edge.topBeyond);
    if (this.bottomEdge) this.bottomEdge.y = h / 2 + PREFAB.edge.bottomBeyond;
    for (const side of this.sides) side.height = h + PREFAB.side.extraHeight;
  }
}

function sprite(texture: Texture | null, label: string, width: number, height: number): Sprite | null {
  if (!texture) return null;
  const s = new Sprite({ texture, anchor: 0.5, label });
  s.width = width;
  s.height = height;
  return s;
}

/** A fade from `from` to 0 on the runner, then hidden. */
async function fadeOut(node: Container, from: number, seconds: number, runner: PresentationTaskRunner, scope: string): Promise<void> {
  node.alpha = from;
  await runner.tweenProgress(seconds, (t) => (node.alpha = from * (1 - t)), scope);
  node.alpha = 0;
  node.visible = false;
}
